// Whole-home composite math — pure, unit-tested, no React/three.
//
// Places each scanned room into a shared whole-home frame and groups rooms into
// floors. Two independent signals (confirmed against real data):
//   • x/z placement  ← ScanPayload.projectTransformValues (the curated whole-home
//     placement: a pure-Y yaw + x/z translation, y == 0). Drives the 2D plan and
//     the horizontal placement of the 3D dollhouse.
//   • floor / elevation ← room.json referenceOriginTransform.translation.y. The
//     curated placement carries NO elevation, so floors are clustered from this
//     raw AR-reference Y. Evidence (Cabin Full split-level): "loft"/upper rooms
//     have a LOW referenceOriginTransform.y and "basement"/"lower" rooms a HIGH
//     one — i.e. physical height DECREASES as referenceOriginTransform.y rises.
//     We cluster by gaps (sign-agnostic) and order so the lowest physical level
//     (highest refY) is index 0 (ground/bottom).

import type { OpeningKind, ScanGeometry, Vec2 } from "../geometry/types";

const IDENTITY = [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1];

// Min vertical gap (m) between referenceOriginTransform.y clusters to count as a
// separate level. Split-levels step by half-flights (~1.1 m here), while a single
// level's per-room capture noise stays well under this, so 0.7 m cleanly splits
// real levels without spurious ones.
const FLOOR_GAP_M = 0.7;

// Stair-flight capture detection (see correctStairTags). A staircase scanned as
// its own room spans two stories: RoomPlan reports its floor at the BOTTOM of the
// flight and a ceiling 1.5–2 stories up. Thresholds sit between a real shaft
// (Carriage House: 5.4–5.6 m ceilings, 5.7 m², one stairs object) and any normal
// room (≤ ~3 m ceilings) or double-height great room (furniture-populated).
const STAIRWELL_MIN_CEILING_M = 4.0;
const STAIRWELL_MAX_AREA_M2 = 12;
const STAIRWELL_MAX_NON_STAIR_OBJECTS = 1;
// A stair capture's tag is judged against the capture immediately before it in
// the SAME walk; beyond this gap the two scans are different sessions whose tag
// scales don't relate, so no correction is attempted.
const STAIR_PREV_CAPTURE_WINDOW_MS = 3 * 60 * 60 * 1000;
// Legacy walks stamped every scan at FINALIZE (one batch, second-truncated ISO), so
// raw timestamps closer than this don't prove capture order — order then falls back
// to the app-assigned "Room N" numbering, which finalize hands out in capture order.
const STAIR_ORDER_TIE_MS = 90 * 1000;

/** The app-assigned capture-order number ("Room 12" → 12), or null once renamed. */
function roomNumber(name: string | undefined): number | null {
  const m = name?.match(/^Room (\d+)$/);
  return m ? parseInt(m[1], 10) : null;
}

/** Input for one scanned room in a project's composite. */
export interface RoomInput {
  scanId: string;
  name: string;
  geometry: ScanGeometry;
  /** projectTransformValues — 16 col-major floats mapping room-local → shared frame. */
  placement?: number[];
  /** referenceOriginTransform.translation.y — the elevation fallback signal. */
  refY?: number;
  /** capture-time floor tag (user marked level changes during the walk) — authoritative when set. */
  captureFloorTag?: number | null;
  /** manual floor index from the project's roomOverridesJSON — the user's hand assignment, wins over all. */
  floorOverride?: number | null;
  /** scan createdAt (epoch ms) — capture order within the walk, for stair-tag correction. */
  capturedAt?: number;
  /** capture-time "this is the staircase" flag (the Scan-the-Stairs affordance). */
  isStairwell?: boolean;
}

/** A room with all its floor-plane geometry mapped into the shared frame. */
export interface PlacedRoom {
  scanId: string;
  name: string;
  floorIndex: number;
  floorPolygon: Vec2[];
  thickWalls: Vec2[][];
  walls: { start: Vec2; end: Vec2 }[];
  openings: { kind: OpeningKind; start: Vec2; end: Vec2 }[];
  objects: { corners: Vec2[]; center: Vec2; label: string; isStairs: boolean }[];
  center: Vec2;
  boundsMin: Vec2;
  boundsMax: Vec2;
  /** placed via a real curated transform (vs a synthetic grid fallback). */
  placedReal: boolean;
  /** The 16-float col-major matrix actually used (real or grid), for the 3D scene. */
  placementMatrix: number[];
}

export interface FloorMeta {
  index: number;
  label: string;
  refY: number;
  scanIds: string[];
}

export interface Composite {
  rooms: PlacedRoom[];
  floors: FloorMeta[];
  /** at least one room used a real (non-identity) curated placement. */
  anyRealPlacement: boolean;
  /** every room used a real placement (no grid fallback rooms). */
  allRealPlacement: boolean;
  multiFloor: boolean;
  boundsMin: Vec2;
  boundsMax: Vec2;
}

/** True when a transform exists, is full-length, and isn't the identity. */
export function isRealPlacement(m?: number[]): boolean {
  if (!m || m.length < 16) return false;
  return !m.every((x, i) => Math.abs(x - IDENTITY[i]) < 1e-6);
}

/**
 * Map a floor-plane point (x = world-X, y = world-Z) through a 16-float
 * column-major placement. For the data's pure-Y rotation the input Y is
 * irrelevant, so this is an exact 2D affine: world = R·p + t on the floor plane.
 */
export function placePoint(p: Vec2, m?: number[]): Vec2 {
  if (!m || m.length < 16) return { x: p.x, y: p.y };
  return {
    x: m[0] * p.x + m[8] * p.y + m[12],
    y: m[2] * p.x + m[10] * p.y + m[14],
  };
}

const avg = (xs: number[]): number => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : 0);

/**
 * Cluster scans into floors by referenceOriginTransform.y gaps. Returns the
 * floor index per scanId and ordered floor metadata (index 0 = lowest physical
 * level). Scans with no refY collapse onto floor 0. A single cluster (or no
 * usable refY) yields one floor.
 */
export function clusterFloors(
  items: { scanId: string; refY?: number }[],
  gap = FLOOR_GAP_M
): { byScan: Map<string, number>; floors: Omit<FloorMeta, "label">[] } {
  const byScan = new Map<string, number>();
  const withY = items.filter((i) => typeof i.refY === "number" && Number.isFinite(i.refY));
  if (withY.length === 0) {
    items.forEach((i) => byScan.set(i.scanId, 0));
    return { byScan, floors: [{ index: 0, refY: 0, scanIds: items.map((i) => i.scanId) }] };
  }
  const sorted = [...withY].sort((a, b) => (a.refY as number) - (b.refY as number));
  const clusters: { scanIds: string[]; ys: number[] }[] = [];
  for (const it of sorted) {
    const last = clusters[clusters.length - 1];
    if (last && (it.refY as number) - last.ys[last.ys.length - 1] <= gap) {
      last.scanIds.push(it.scanId);
      last.ys.push(it.refY as number);
    } else {
      clusters.push({ scanIds: [it.scanId], ys: [it.refY as number] });
    }
  }
  // Physical height decreases as refY rises → order by DESCENDING refY so the
  // lowest level (highest refY: basement/lower) becomes index 0 (bottom).
  clusters.sort((a, b) => avg(b.ys) - avg(a.ys));
  const floors = clusters.map((c, index) => {
    c.scanIds.forEach((id) => byScan.set(id, index));
    return { index, refY: avg(c.ys), scanIds: c.scanIds };
  });
  // Scans lacking refY fall onto the ground floor.
  items.filter((i) => !(typeof i.refY === "number" && Number.isFinite(i.refY)))
    .forEach((i) => byScan.set(i.scanId, 0));
  return { byScan, floors };
}

/**
 * A capture that is really a staircase scanned as its own "room": at least one
 * stairs object, (almost) nothing else, a small footprint, and a ceiling well
 * over one story — the two-level shaft seen from inside the flight.
 */
export function isStairwellCapture(g: ScanGeometry): boolean {
  const stairs = g.objects.filter((o) => o.isStairs).length;
  if (stairs < 1) return false;
  return (
    g.objects.length - stairs <= STAIRWELL_MAX_NON_STAIR_OBJECTS &&
    g.ceilingHeightMeters >= STAIRWELL_MIN_CEILING_M &&
    g.floorAreaSquareMeters <= STAIRWELL_MAX_AREA_M2
  );
}

/**
 * Correct the floor tag of stair-flight captures so the shaft anchors at the
 * BOTTOM of the flight (a stair capture spans two levels but carries one tag).
 *
 * An ASCENT capture is mis-tagged: the user taps ↑ (arrival level) and then
 * scans the stairs on the way up, so the capture — whose floor is physically on
 * the level below — gets the upper level's tag and renders a story too high (the
 * phantom-level bug). A DESCENT capture already carries the lower level's tag.
 * The two are distinguished by walk direction: a stair capture whose tag is
 * HIGHER than the previous capture's tag was reached climbing (the delta can
 * exceed 1 — Carriage House re-climbed from the basement, −1 → +1, capturing
 * nothing on the level between), so it anchors one below its tag; a lower or
 * equal previous tag means descent or the already-correct affordance flow.
 *
 * Capture order: legacy walks stamped every scan at finalize (one same-second
 * batch), so adjacent entries are trusted only when their timestamps are far
 * enough apart to be real capture spacing OR both still carry the app-assigned
 * "Room N" numbers, which finalize hands out in capture order. Untrusted
 * adjacency skips the correction (fail safe: the manual override still works).
 *
 * Pure and read-only; returns corrected raw tags keyed by scanId (only entries
 * that changed). Raw (pre-correction) previous tags are deliberately used so
 * back-to-back flights (a split-level switchback) each anchor one level apart.
 * Mirrors ProjectAssembly.stairCorrectedTags on iOS — keep the two in agreement.
 */
export function correctStairTags(inputs: RoomInput[]): Map<string, number> {
  const out = new Map<string, number>();
  const tagged = inputs
    .filter(
      (i) => typeof i.captureFloorTag === "number" && typeof i.capturedAt === "number" && Number.isFinite(i.capturedAt),
    )
    .map((i) => ({ input: i, at: i.capturedAt as number, tag: i.captureFloorTag as number, num: roomNumber(i.name) }))
    .sort(
      (a, b) =>
        a.at - b.at ||
        (a.num ?? Number.MAX_SAFE_INTEGER) - (b.num ?? Number.MAX_SAFE_INTEGER) ||
        a.input.scanId.localeCompare(b.input.scanId),
    );
  for (let k = 1; k < tagged.length; k++) {
    const cur = tagged[k];
    if (!(cur.input.isStairwell || isStairwellCapture(cur.input.geometry))) continue;
    const prev = tagged[k - 1];
    const gap = cur.at - prev.at;
    if (gap > STAIR_PREV_CAPTURE_WINDOW_MS) continue;
    const orderTrusted = gap >= STAIR_ORDER_TIE_MS || (cur.num !== null && prev.num !== null && cur.num !== prev.num);
    if (!orderTrusted) continue;
    if (prev.tag < cur.tag) out.set(cur.input.scanId, cur.tag - 1);
  }
  return out;
}

/**
 * Resolve each room's floor index, honoring the SAME authoritative signals the iOS app uses
 * (ProjectAssembly.assignFloors + applyFloorOverrides) so the web agrees with the dollhouse:
 *   1. captureFloorTag — if any room carries one, tags are authoritative (the user marked level
 *      changes during the walk); stair-flight captures first re-anchor to the bottom of the
 *      flight (correctStairTags); raw tags normalize to contiguous 0-based indices (lowest = 0).
 *   2. else refY clustering (clusterFloors) — the elevation fallback.
 *   3. manual floor overrides (roomOverridesJSON) ALWAYS win — the user's hand-assigned story,
 *      needed because split-levels can't be auto-clustered.
 * Floor labels use the user's custom level names (overrides.levels) when present.
 *
 * The no-tag/no-override path is byte-identical to the old refY-only clustering.
 */
export function resolveFloors(
  inputs: RoomInput[],
  levelNames?: Record<string, string>,
): { byScan: Map<string, number>; floors: FloorMeta[] } {
  const named = (index: number, ordinal: number, total: number) =>
    levelNames?.[String(index)]?.trim() || floorLabel(ordinal, total);

  const hasTags = inputs.some((i) => typeof i.captureFloorTag === "number");
  const hasOverrides = inputs.some((i) => typeof i.floorOverride === "number");

  if (!hasTags && !hasOverrides) {
    const { byScan, floors } = clusterFloors(inputs.map((r) => ({ scanId: r.scanId, refY: r.refY })));
    return {
      byScan,
      floors: floors.map((f) => ({ ...f, label: named(f.index, f.index, floors.length) })),
    };
  }

  const byScan = new Map<string, number>();
  if (hasTags) {
    const corrected = correctStairTags(inputs);
    const tagOf = (i: RoomInput): number | undefined => {
      const t = corrected.get(i.scanId);
      if (typeof t === "number") return t;
      return typeof i.captureFloorTag === "number" ? i.captureFloorTag : undefined;
    };
    const tags = Array.from(
      new Set(inputs.map(tagOf).filter((t): t is number => typeof t === "number")),
    ).sort((a, b) => a - b);
    const indexOf = new Map(tags.map((t, i) => [t, i] as const));
    inputs.forEach((i) => {
      const t = tagOf(i);
      byScan.set(i.scanId, typeof t === "number" ? indexOf.get(t) ?? 0 : 0);
    });
  } else {
    const { byScan: ref } = clusterFloors(inputs.map((r) => ({ scanId: r.scanId, refY: r.refY })));
    inputs.forEach((i) => byScan.set(i.scanId, ref.get(i.scanId) ?? 0));
  }
  // Manual overrides always win (split-level hand assignment).
  for (const i of inputs) if (typeof i.floorOverride === "number") byScan.set(i.scanId, i.floorOverride);

  const indices = Array.from(new Set([...byScan.values()])).sort((a, b) => a - b);
  const floors: FloorMeta[] = indices.map((index, ordinal) => {
    const scanIds = [...byScan.entries()].filter(([, v]) => v === index).map(([k]) => k);
    const ys = inputs
      .filter((i) => scanIds.includes(i.scanId))
      .map((i) => i.refY)
      .filter((y): y is number => typeof y === "number");
    return { index, label: named(index, ordinal, indices.length), refY: avg(ys), scanIds };
  });
  return { byScan, floors };
}

/** Human label for a floor given its index and the total count. */
export function floorLabel(index: number, total: number): string {
  if (total <= 1) return "Whole home";
  if (index === 0 && total === 2) return "Lower level";
  if (index === total - 1 && total === 2) return "Upper level";
  return `Level ${index + 1}`;
}

/** Local floor-plane bounds of a room (floor polygon, else wall endpoints). */
function localBounds(g: ScanGeometry): { min: Vec2; max: Vec2 } {
  const pts: Vec2[] =
    g.floorPolygon.length >= 3
      ? g.floorPolygon
      : g.walls.flatMap((w) => [w.start, w.end]);
  if (pts.length === 0) return { min: { x: -1, y: -1 }, max: { x: 1, y: 1 } };
  let min = { ...pts[0] };
  let max = { ...pts[0] };
  for (const p of pts) {
    min = { x: Math.min(min.x, p.x), y: Math.min(min.y, p.y) };
    max = { x: Math.max(max.x, p.x), y: Math.max(max.y, p.y) };
  }
  return { min, max };
}

/**
 * Lay rooms with no curated placement into a tidy non-overlapping grid (row
 * wrap ≈ √n columns), returning a translation-only placement matrix per room
 * that drops each room's local bbox-min at its grid cell. The whole grid is
 * shifted by `originZ` so it can sit below a real-placed cluster.
 */
export function gridPlacements(
  geoms: ScanGeometry[],
  gapM = 1.0,
  originZ = 0
): number[][] {
  const n = geoms.length;
  if (n === 0) return [];
  const cols = Math.max(1, Math.ceil(Math.sqrt(n)));
  const bounds = geoms.map(localBounds);
  const sizes = bounds.map((b) => ({ w: b.max.x - b.min.x, h: b.max.y - b.min.y }));
  const out: number[][] = [];
  let cellX = 0;
  let rowZ = originZ;
  let rowH = 0;
  let col = 0;
  for (let i = 0; i < n; i++) {
    if (col >= cols) {
      col = 0;
      cellX = 0;
      rowZ += rowH + gapM;
      rowH = 0;
    }
    const dx = cellX - bounds[i].min.x;
    const dz = rowZ - bounds[i].min.y;
    const m = IDENTITY.slice();
    m[12] = dx;
    m[14] = dz;
    out.push(m);
    cellX += sizes[i].w + gapM;
    rowH = Math.max(rowH, sizes[i].h);
    col += 1;
  }
  return out;
}

/** Transform one room's floor-plane geometry into the shared frame. */
export function placeRoom(
  input: RoomInput,
  matrix: number[] | undefined,
  floorIndex: number,
  placedReal: boolean
): PlacedRoom {
  const g = input.geometry;
  const tp = (p: Vec2) => placePoint(p, matrix);
  const floorPolygon = g.floorPolygon.map(tp);
  const thickWalls = g.thickWalls.map((tw) => tw.polygon.map(tp));
  const walls = g.walls.map((w) => ({ start: tp(w.start), end: tp(w.end) }));
  const openings: PlacedRoom["openings"] = [];
  for (const d of g.wallDecompositions) {
    for (const s of d.slots) {
      openings.push({ kind: s.opening.kind, start: tp(s.startPoint), end: tp(s.endPoint) });
    }
  }
  const objects = g.objects.map((o) => ({
    corners: o.corners.map(tp),
    center: tp(o.center),
    label: o.label,
    isStairs: o.isStairs,
  }));

  const pts: Vec2[] = floorPolygon.length >= 3 ? floorPolygon : walls.flatMap((w) => [w.start, w.end]);
  let boundsMin: Vec2;
  let boundsMax: Vec2;
  if (pts.length === 0) {
    boundsMin = { x: -1, y: -1 };
    boundsMax = { x: 1, y: 1 };
  } else {
    boundsMin = { ...pts[0] };
    boundsMax = { ...pts[0] };
    for (const p of pts) {
      boundsMin = { x: Math.min(boundsMin.x, p.x), y: Math.min(boundsMin.y, p.y) };
      boundsMax = { x: Math.max(boundsMax.x, p.x), y: Math.max(boundsMax.y, p.y) };
    }
  }
  const center =
    floorPolygon.length >= 3
      ? {
          x: floorPolygon.reduce((a, p) => a + p.x, 0) / floorPolygon.length,
          y: floorPolygon.reduce((a, p) => a + p.y, 0) / floorPolygon.length,
        }
      : { x: (boundsMin.x + boundsMax.x) / 2, y: (boundsMin.y + boundsMax.y) / 2 };

  return {
    scanId: input.scanId,
    name: input.name,
    floorIndex,
    floorPolygon,
    thickWalls,
    walls,
    openings,
    objects,
    center,
    boundsMin,
    boundsMax,
    placedReal,
    placementMatrix: matrix && matrix.length >= 16 ? matrix : IDENTITY.slice(),
  };
}

/**
 * Build the whole-home composite: cluster floors, place every room into the
 * shared frame (curated transform where present, else a grid fallback laid out
 * below the real cluster), and union the bounds.
 */
export function buildComposite(inputs: RoomInput[], levelNames?: Record<string, string>): Composite {
  const anyRealPlacement = inputs.some((r) => isRealPlacement(r.placement));
  const allRealPlacement = inputs.length > 0 && inputs.every((r) => isRealPlacement(r.placement));

  const { byScan, floors } = resolveFloors(inputs, levelNames);

  // Rooms without a real placement get grid cells. If the project mixes real +
  // unplaced rooms, the grid is dropped BELOW the real-placed cluster's bounds.
  const unplaced = inputs.filter((r) => !isRealPlacement(r.placement));
  let gridOriginZ = 0;
  if (anyRealPlacement && unplaced.length > 0) {
    let maxZ = -Infinity;
    for (const r of inputs) {
      if (!isRealPlacement(r.placement)) continue;
      const placed = placeRoom(r, r.placement, 0, true);
      maxZ = Math.max(maxZ, placed.boundsMax.y);
    }
    gridOriginZ = Number.isFinite(maxZ) ? maxZ + 2 : 0;
  }
  const grid = gridPlacements(unplaced.map((r) => r.geometry), 1.0, gridOriginZ);
  const gridByScan = new Map<string, number[]>();
  unplaced.forEach((r, i) => gridByScan.set(r.scanId, grid[i]));

  const rooms = inputs.map((r) => {
    const real = isRealPlacement(r.placement);
    const matrix = real ? r.placement : gridByScan.get(r.scanId);
    return placeRoom(r, matrix, byScan.get(r.scanId) ?? 0, real);
  });

  let boundsMin: Vec2 = { x: Infinity, y: Infinity };
  let boundsMax: Vec2 = { x: -Infinity, y: -Infinity };
  for (const room of rooms) {
    boundsMin = { x: Math.min(boundsMin.x, room.boundsMin.x), y: Math.min(boundsMin.y, room.boundsMin.y) };
    boundsMax = { x: Math.max(boundsMax.x, room.boundsMax.x), y: Math.max(boundsMax.y, room.boundsMax.y) };
  }
  if (!Number.isFinite(boundsMin.x)) {
    boundsMin = { x: -1, y: -1 };
    boundsMax = { x: 1, y: 1 };
  }

  return {
    rooms,
    floors,
    anyRealPlacement,
    allRealPlacement,
    multiFloor: floors.length > 1,
    boundsMin,
    boundsMax,
  };
}
